/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import {
  Dropdown,
  Button,
  Menu,
  Row,
  Col,
  theme as duxtonTheme,
} from "@duxton-web/ui"
import {
  AddCircleFilled,
  ChevronDownUniversal,
  EditableFilled,
} from "@duxton-web/icons"
import { SellingTime } from "types/models/menu"
import { OverflowTip } from "components/common"
import {
  getIsAlwaysAvailable,
  getIsAllSellingTime,
} from "utils/selling_time_helper"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { theme } from "styles/theme"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import {
  dropdownMenuItemStyle,
  dropdownButton,
  dropdownEditButton,
} from "./dropdownStyles"
import { SellingTimePeriods } from "./SellingTimePeriods"
import styles from "./SellingTimeDropdown.modules.css"

const { useToken } = duxtonTheme

export interface EventTrackerObject {
  eventCategoryTracker: EventCategory
  categoryID?: string
  itemID?: string
  itemIDs?: string[]
  isMultipleSelect?: boolean
}
interface SellingTimeDropdownProps {
  sellingTimes?: SellingTime[]
  selectedSellingTime?: SellingTime
  disabled?: boolean
  isEditable?: boolean
  eventTrackerObject?: EventTrackerObject
  onChange?: (selectedSellingTime?: SellingTime) => void
}

interface SellingTimeRowProps {
  sellingTime: SellingTime
  onCloseDropdown: () => void
  isEditable?: boolean
  isSelected?: boolean
}

interface AddSellingTimeProps {
  eventTrackerObject?: EventTrackerObject
  onClickAdd: () => void
}

export const SellingTimeRow: FC<SellingTimeRowProps> = ({
  sellingTime,
  onCloseDropdown,
  isEditable,
  isSelected,
}: SellingTimeRowProps) => {
  const textMap = useTranslationsMapTyped({
    allSellingTime: "menu.sellingTime.dropdown.allSellingTime",
    alwaysAvailable: "menu.sellingTime.dropdown.alwaysAvailable",
  })

  const isAllSellingTime = getIsAllSellingTime(sellingTime)
  const isAlwaysAvailable = getIsAlwaysAvailable(sellingTime)

  const getSellingTimeName = () => {
    if (isAllSellingTime) return textMap.allSellingTime
    if (isAlwaysAvailable) return textMap.alwaysAvailable
    return sellingTime?.name
  }

  const dispatch = useDispatch()

  return (
    <Row align="middle" justify="space-between" gutter={16}>
      <Col>
        <span
          css={css`
            font-size: 16px;
            ${isSelected
              ? css`
                  font-weight: 500;
                `
              : ""}
          `}
        >
          {getSellingTimeName()}
        </span>
        {!isAllSellingTime && <SellingTimePeriods sellingTime={sellingTime} />}
      </Col>
      {isEditable && !isAllSellingTime && !isAlwaysAvailable && (
        <Col>
          <Button
            className="btnEdit"
            css={dropdownEditButton}
            size="small"
            shape="circle"
            onClick={e => {
              e.stopPropagation()
              dispatch(
                actionCreator.menu.toggleDrawer({
                  sellingTime,
                  showSellingTimeEditor: true,
                }),
              )
              onCloseDropdown()
            }}
          >
            <EditableFilled />
          </Button>
        </Col>
      )}
    </Row>
  )
}

export const AddSellingTime: FC<AddSellingTimeProps> = ({
  eventTrackerObject,
  onClickAdd,
}: AddSellingTimeProps) => {
  const {
    sellingTimes,
    merchantID,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const textMap = useTranslationsMapTyped({
    showCreate: "menu.sellingTime.button.showCreate",
  })

  const dispatch = useDispatch()

  const handleClickAdd = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        sellingTime: null,
        showSellingTimeEditor: true,
      }),
    )

    if (eventTrackerObject) {
      TroyWebTracker.sendEvent({
        category: eventTrackerObject.eventCategoryTracker,
        action: EventAction.addSellingTime,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          ...(eventTrackerObject?.categoryID && {
            CATEGORY_ID: eventTrackerObject?.categoryID,
          }),
          ...(eventTrackerObject?.itemID && {
            ITEM_ID: eventTrackerObject?.itemID,
          }),
          ...(eventTrackerObject?.itemIDs && {
            ITEM_ID: eventTrackerObject?.itemIDs,
            NUM_ITEM: eventTrackerObject?.itemIDs.length,
          }),
          SELLING_TIME_COUNT: sellingTimes?.length,
        },
      })
    }

    onClickAdd()
  }

  return (
    <div
      onClick={handleClickAdd}
      css={css`
        padding: 21px 0;
        display: flex;
        justify-content: center;
        background-color: white;
        cursor: pointer;
        border-top: solid 1px ${theme.colors.border};
        border-radius: 0 0 14px 14px;
      `}
    >
      <span
        css={css`
          font-size: 16px;
        `}
      >
        <AddCircleFilled
          css={css`
            font-size: 20px;
            color: #00a5cf;
            margin-right: 7px;
          `}
        />
        {textMap.showCreate}
      </span>
    </div>
  )
}

export const SellingTimeDropdown: FC<SellingTimeDropdownProps> = ({
  sellingTimes,
  selectedSellingTime,
  disabled,
  isEditable = false,
  eventTrackerObject,
  onChange,
}: SellingTimeDropdownProps) => {
  const textMap = useTranslationsMapTyped({
    placeholder: "menu.sellingTime.placeholder.sellingTime",
    allSellingTime: "menu.sellingTime.dropdown.allSellingTime",
    alwaysAvailable: "menu.sellingTime.dropdown.alwaysAvailable",
  })

  const { token } = useToken()

  const {
    isEditAllowed,
    merchantID,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const [visible, setVisible] = useState(false)

  const isAllSellingTime = getIsAllSellingTime(selectedSellingTime)

  const isAlwaysAvailable = getIsAlwaysAvailable(selectedSellingTime)

  const getSellingTimeName = () => {
    if (isAllSellingTime) return textMap.allSellingTime
    if (isAlwaysAvailable) return textMap.alwaysAvailable
    return selectedSellingTime?.name
  }

  const visibleChangeHandler = (_visible: boolean) => {
    if (_visible && eventTrackerObject) {
      TroyWebTracker.sendEvent({
        category: eventTrackerObject.eventCategoryTracker,
        action: EventAction.clickSellingTime,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          ...(eventTrackerObject?.categoryID && {
            CATEGORY_ID: eventTrackerObject?.categoryID,
          }),
          ...(eventTrackerObject?.itemID && {
            ITEM_ID: eventTrackerObject?.itemID,
          }),
          ...(eventTrackerObject?.itemIDs && {
            ITEM_ID: eventTrackerObject?.itemIDs,
            NUM_ITEM: eventTrackerObject?.itemIDs.length,
          }),
          ...(selectedSellingTime && {
            SELLING_TIME_NAME: selectedSellingTime.name,
            SELLING_TIME_ID: selectedSellingTime.sellingTimeID,
          }),
          ...(eventTrackerObject.isMultipleSelect && {
            HAS_SELLING_TIME: !!selectedSellingTime,
          }),
          SELLING_TIME_COUNT: sellingTimes?.length,
        },
      })
    }

    setVisible(_visible)
  }

  const selectDropdownMenuHandler = (e: any) => {
    if (e.key === "clear" && onChange) {
      onChange()
    } else if (onChange) {
      const newSelectedSellingTime = sellingTimes?.find(
        sellingTime => sellingTime.sellingTimeID === e.key,
      )
      if (
        newSelectedSellingTime &&
        newSelectedSellingTime.sellingTimeID !==
          selectedSellingTime?.sellingTimeID
      ) {
        onChange(newSelectedSellingTime)

        if (eventTrackerObject) {
          TroyWebTracker.sendEvent({
            category: eventTrackerObject.eventCategoryTracker,
            action: EventAction.selectSellingTime,
            labels: {
              ...(merchantID
                ? { STORE_ID: merchantID }
                : {
                    MERCHANT_GROUP_ID: merchantGroupID,
                    MENU_GROUP_ID: menuGroupID,
                  }),
              ...(eventTrackerObject?.categoryID && {
                CATEGORY_ID: eventTrackerObject?.categoryID,
              }),
              ...(eventTrackerObject?.itemID && {
                ITEM_ID: eventTrackerObject?.itemID,
              }),
              ...(eventTrackerObject?.itemIDs && {
                ITEM_ID: eventTrackerObject?.itemIDs,
                NUM_ITEM: eventTrackerObject?.itemIDs.length,
              }),
              ...(eventTrackerObject.isMultipleSelect && {
                HAS_PREVIOUS_SELLING_TIME: !!selectedSellingTime,
              }),
              SELLING_TIME_NAME: newSelectedSellingTime?.name,
              SELLING_TIME_ID: newSelectedSellingTime?.sellingTimeID,
              SELLING_TIME_COUNT: sellingTimes?.length,
            },
          })
        }
      }
    }
    setVisible(false)
  }

  return (
    <Dropdown
      disabled={disabled}
      trigger={["click"]}
      open={visible}
      onOpenChange={visibleChangeHandler}
      overlayClassName={styles.dropdownOverlay}
      overlayStyle={{ boxShadow: token.boxShadow, borderRadius: "14px" }}
      data-testid="selling-time-dropdown"
      overlay={
        <Fragment>
          <Menu
            onClick={selectDropdownMenuHandler}
            data-testid="selling-time-dropdown-menu"
            selectable
          >
            {sellingTimes?.map(sellingTime => (
              <Menu.Item
                key={sellingTime.sellingTimeID}
                css={css`
                  ${dropdownMenuItemStyle}
                  &:not(:hover) {
                    .btnEdit {
                      visibility: hidden;
                    }
                  }
                  height: auto;
                `}
              >
                <SellingTimeRow
                  sellingTime={sellingTime}
                  onCloseDropdown={() => {
                    setVisible(false)
                  }}
                  isEditable={isEditable}
                  isSelected={
                    sellingTime.sellingTimeID ===
                    selectedSellingTime?.sellingTimeID
                  }
                />
              </Menu.Item>
            ))}
          </Menu>
          {isEditAllowed && (
            <AddSellingTime
              onClickAdd={() => setVisible(false)}
              eventTrackerObject={eventTrackerObject}
            />
          )}
        </Fragment>
      }
    >
      <Button
        data-testid="selling-time-dropdown-btn"
        css={css`
          ${dropdownButton}
          min-width: 0;
        `}
        shape="default"
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: end;
            ${!selectedSellingTime?.name
              ? css`
                  color: ${theme.colors.placeholder};
                `
              : ""}
          `}
        >
          <OverflowTip>
            {getSellingTimeName() ?? textMap.placeholder}
          </OverflowTip>
          <span
            css={css`
              flex: 1 1 0px;
            `}
          />
          <ChevronDownUniversal />
        </div>
      </Button>
    </Dropdown>
  )
}
